import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import Title from '../components/global/title';
import { Spinner } from '@blueprintjs/core';
import NotLoggedIn from '../components/home/notloggedin/notloggedin';
import GameGamesList from '../components/game/gameslist';
import PullToRefresh from '../components/global/pulltorefresh';
import { useActiveGamesProvider } from '../components/global/activegamesprovider';
import HomeLiveGames from '../components/home/livegames';
import { getServerSession } from 'next-auth';
import { authOptions } from './api/auth/[...nextauth]';
import ServicePropsHomepage from '../src/server/models/game/services/hydrators/homepage';
import Game from '../src/models/game';
import HomeNews from '@/components/home/news';
import type { ApiNewsPost } from '@/src/types/apinews';
import HomeSectionHeading from '@/components/home/sectionheading';
import { IconNames } from '@blueprintjs/icons';
import HomeCreateGameCard from '@/components/home/creategamecard';
import { HOMEPAGE_CREATE_GAME_CARD_SUBTITLES, HOMEPAGE_CREATE_GAME_CARD_TITLES } from '@/src/util/home';
import styles from './index.module.css';

export const getServerSideProps = (async (context) => {
    const { req, res } = context;
    const session = await getServerSession(req, res, authOptions);

    if (!session) {
        return {
            props: {},
        };
    }

    const userId = session.user.id;
    const fromServer = await ServicePropsHomepage(userId);

    return {
        props: fromServer,
    };
});

export default function Homepage(
    {
        newsPosts = [],
        randomCreateGameCardTitle = HOMEPAGE_CREATE_GAME_CARD_TITLES[0],
        randomCreateGameCardSubtitle = HOMEPAGE_CREATE_GAME_CARD_SUBTITLES[0],
    }: {
        newsPosts?: ApiNewsPost[];
        randomCreateGameCardTitle?: string;
        randomCreateGameCardSubtitle?: string;
    },
): JSX.Element {
    const { data: session, status } = useSession();
    const router = useRouter();
    const { activeGames: allActiveGames, openGames } = useActiveGamesProvider();

    const onRefresh = useCallback(
        () => {
            router.reload();
        },
        [router],
    );

    useEffect(
        () => {
            if (router.query.error) {
                console.error(router.query.error);
            }
        },
        [router],
    );

    if (status === 'loading') {
        return <><Title subtitle="Loading..." /><Spinner /></>;
    } else if (status === 'unauthenticated') {
        return <NotLoggedIn />;
    }

    const userId = session.user.id;
    const activeGames = allActiveGames.filter(
        game => game.activeSeats.some(
            seat => seat.userId === userId
        ) || game.invites?.find(
            invite => invite.user?.id === userId
        ) || (new Game(game)).isGameOverAndUnseen(userId),
    );

    return (
        <div id="content">
            <PullToRefresh onRefresh={onRefresh}>
                <Title />
                <div className={styles.contentColumn}>
                    {
                        activeGames.length > 0 &&
                        <HomeSectionHeading
                            count={activeGames.length}
                            icon={IconNames.Lightning}
                            title="Your Turn"
                        />
                    }
                    <GameGamesList
                        games={activeGames}
                        userId={userId}
                    />
                    <HomeSectionHeading
                        icon={IconNames.Add}
                        title="Start a Game"
                    />
                    <HomeCreateGameCard
                        title={randomCreateGameCardTitle}
                        subtitle={randomCreateGameCardSubtitle}
                    />
                    {
                        openGames?.length > 0 &&
                        <HomeSectionHeading
                            count={openGames.length}
                            icon={IconNames.Globe}
                            title="Open Games"
                        />
                    }
                    <GameGamesList
                        games={openGames}
                        userId={userId}
                    />
                    <HomeLiveGames />
                    <HomeNews newsPosts={newsPosts} />
                </div>
            </PullToRefresh>
        </div>
    );
}
